import { tierToRagBand } from "../../../components/RFIs/RiskSummaryCell";

/**
 * What the details header and the Risk Overview card show for a run's overall
 * score. A run whose questions are all still unscored carries the NotScored
 * tier and no band; it has no score to show, so the surfaces say so instead
 * of painting a vacuous 100% Green.
 */
export function describeRiskScore(snapshot) {
  if (!snapshot) return { hasScore: false, rag: null, scoreText: "No score" };

  const tier = String(snapshot.overall_tier || "").trim();
  const rag = tierToRagBand(tier);
  if (!rag || tier.toLowerCase() === "notscored") {
    return { hasScore: false, rag: null, scoreText: "No score" };
  }

  const score = Number(snapshot.overall_score);
  if (!Number.isFinite(score)) return { hasScore: false, rag: null, scoreText: "No score" };

  return { hasScore: true, rag, scoreText: `${score.toFixed(1)}%` };
}

/**
 * The reason a run cannot be published yet, or null when it can: questions
 * still waiting for a score come first, then findings missing gap text.
 */
export function publishBlocker({ unscoredCount = 0, missingGapTextCount = 0, requireGapText = false } = {}) {
  if (unscoredCount > 0) {
    return {
      kind: "unscored",
      count: unscoredCount,
      label: `${unscoredCount} question${unscoredCount === 1 ? "" : "s"} not scored`,
    };
  }
  if (requireGapText && missingGapTextCount > 0) {
    return {
      kind: "gapText",
      count: missingGapTextCount,
      label: `${missingGapTextCount} finding${missingGapTextCount === 1 ? "" : "s"} need gap text`,
    };
  }
  return null;
}

/**
 * The notification for a publish the server refused (422): unscored questions
 * first, then findings missing gap text. `rescored` words it for the apply
 * flow, where the model was already applied and rescored before the refusal.
 */
export function describePublishProblem(validation, mode, { rescored = false } = {}) {
  const unscored = Number(validation?.unscored_question_count) || 0;
  if (unscored > 0) {
    const noun = unscored === 1 ? "question is" : "questions are";
    return {
      title: rescored ? "Rescored, but scoring is incomplete" : "Publish blocked: scoring incomplete",
      desc: `${unscored} ${noun} not scored yet. Score the questionnaire before publishing.`,
    };
  }

  const count = Number(validation?.blocking_finding_count) || 0;
  const findings = `${count} non-Compliant finding${count === 1 ? "" : "s"}`;
  return rescored
    ? {
        title: "Rescored, but publish needs gap notes",
        desc: `The model was applied and rescored, but ${findings} still need gap notes before it can be published in ${mode} mode. Add them, then Publish.`,
      }
    : {
        title: "Publish blocked: gap text required",
        desc: `${findings} still need gap notes before publishing in ${mode} mode.`,
      };
}
